import {
    ReviewsDeleteApiFailureAction,
    ReviewsDeleteApiRequestAction,
    ReviewsDeleteApiSuccessAction,
    ReviewsDeleteReplyApiFailureAction,
    ReviewsDeleteReplyApiRequestAction,
    ReviewsDeleteReplyApiSuccessAction,
    ReviewsLoadApiFailureAction,
    ReviewsLoadApiRequestAction,
    ReviewsLoadApiSuccessAction,
    REVIEWS_DELETE_API_FAILURE,
    REVIEWS_DELETE_API_REQUEST,
    REVIEWS_DELETE_API_SUCCESS,
    REVIEWS_DELETE_REPLY_API_FAILURE,
    REVIEWS_DELETE_REPLY_API_REQUEST,
    REVIEWS_DELETE_REPLY_API_SUCCESS,
    REVIEWS_LOAD_API_FAILURE,
    REVIEWS_LOAD_API_REQUEST,
    REVIEWS_LOAD_API_SUCCESS,
} from 'mk2/apps/bazaar/containers/Reviews/Reviews.actions';
import { ReviewRole } from 'mk2/apps/bazaar/schemas';
import infiniteList, {
    initialState as infiniteListInitialState,
    State as InfiniteListState,
} from 'mk2/reducers/infiniteList';
import processing from 'mk2/reducers/processing';
import { Reducer } from 'redux';

export interface ReviewsState extends InfiniteListState {}

export interface PerSellerState {
    sellerId: number;
    [ReviewRole.BUYER]: ReviewsState;
    [ReviewRole.SELLER]: ReviewsState;
}

export const initialPerSellerState: PerSellerState = {
    sellerId: null,
    [ReviewRole.BUYER]: infiniteListInitialState,
    [ReviewRole.SELLER]: infiniteListInitialState,
};

type ReviewsActions =
    | ReviewsLoadApiRequestAction
    | ReviewsLoadApiSuccessAction
    | ReviewsLoadApiFailureAction
    | ReviewsDeleteApiSuccessAction;

const perSellerReducer: Reducer<PerSellerState> = (
    state: PerSellerState = initialPerSellerState,
    action: ReviewsActions,
) => {
    switch (action.type) {
        case REVIEWS_LOAD_API_REQUEST:
        case REVIEWS_LOAD_API_FAILURE:
            return {
                ...state,
                [action.role]: infiniteList<
                    ReviewsLoadApiRequestAction,
                    ReviewsLoadApiSuccessAction,
                    ReviewsLoadApiFailureAction
                >('REVIEWS_LOAD_API', (a) => a.response.result.reviews)(
                    state[action.role],
                    action,
                ),
            };
        case REVIEWS_LOAD_API_SUCCESS:
            return {
                ...state,
                sellerId: action.response.result.seller,
                [action.role]: infiniteList<
                    ReviewsLoadApiRequestAction,
                    ReviewsLoadApiSuccessAction,
                    ReviewsLoadApiFailureAction
                >('REVIEWS_LOAD_API', (a) => a.response.result.reviews)(
                    state[action.role],
                    action,
                ),
            };
        case REVIEWS_DELETE_API_SUCCESS:
            return {
                ...state,
                [action.role]: {
                    ...state[action.role],
                    // Filter out deleted review
                    ids: state[action.role].ids.filter((id) => id !== action.reviewId),
                },
            };
        default:
            return state;
    }
};

export interface State {
    sellers: {
        [username: string]: PerSellerState;
    };
    deletingIds: number[];
    deletingRepliesIds: number[];
}

export const initialState: State = {
    sellers: {},
    deletingIds: [],
    deletingRepliesIds: [],
};

type DeletingActions =
    | ReviewsDeleteApiRequestAction
    | ReviewsDeleteApiSuccessAction
    | ReviewsDeleteApiFailureAction;

type DeletingReplyActions =
    | ReviewsDeleteReplyApiRequestAction
    | ReviewsDeleteReplyApiSuccessAction
    | ReviewsDeleteReplyApiFailureAction;

const reducer: Reducer<State> = (
    state: State = initialState,
    action: ReviewsActions | DeletingActions | DeletingReplyActions,
) => {
    switch (action.type) {
        case REVIEWS_LOAD_API_REQUEST:
        case REVIEWS_LOAD_API_SUCCESS:
        case REVIEWS_LOAD_API_FAILURE:
            return {
                ...state,
                sellers: {
                    ...state.sellers,
                    [action.username]: perSellerReducer(state.sellers[action.username], action),
                },
            };
        case REVIEWS_DELETE_API_REQUEST:
        case REVIEWS_DELETE_API_FAILURE:
            return {
                ...state,
                deletingIds: processing<DeletingActions>(
                    'REVIEWS_DELETE_API',
                    (act) => act.reviewId,
                )(state.deletingIds, action),
            };
        case REVIEWS_DELETE_API_SUCCESS:
            return {
                ...state,
                sellers: {
                    ...state.sellers,
                    [action.username]: perSellerReducer(state.sellers[action.username], action),
                },
                deletingIds: processing<DeletingActions>(
                    'REVIEWS_DELETE_API',
                    (act) => act.reviewId,
                )(state.deletingIds, action),
            };
        case REVIEWS_DELETE_REPLY_API_REQUEST:
        case REVIEWS_DELETE_REPLY_API_SUCCESS:
        case REVIEWS_DELETE_REPLY_API_FAILURE:
            return {
                ...state,
                deletingRepliesIds: processing<DeletingReplyActions>(
                    'REVIEWS_DELETE_REPLY_API',
                    (act) => act.reviewId,
                )(state.deletingRepliesIds, action),
            };
        default:
            return state;
    }
};

export default reducer;
